import React, { useState } from 'react';
import { CategorySpending, Transaction } from '../mocks/mockData';
import { clsx } from 'clsx';
import { PieChart, ChevronDown, ChevronRight } from 'lucide-react';

interface CategoryChartProps {
  categoryData: (CategorySpending & { transactions?: Transaction[] })[];
  monthlyIncome?: number;
  className?: string;
}

export function CategoryChart({ categoryData, monthlyIncome, className }: CategoryChartProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleExpand = (categoryName: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Handle empty data
  if (!categoryData || categoryData.length === 0) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 shadow-sm', className)}>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Spending by Category</h3>
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
            <PieChart className="h-8 w-8 text-gray-400 dark:text-gray-500" />
          </div>
          <p className="text-gray-600 dark:text-gray-400 font-medium">No spending data available</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">Add some expenses to see your category breakdown</p>
        </div>
      </div>
    );
  }

  return (
    <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-6 shadow-sm', className)}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Spending by Category</h3>
      
      {/* Pie Chart Representation */}
      <div className="flex flex-col items-center mb-6">
        {/* Chart Container */}
        <div className="flex-shrink-0 mb-6">
          <div className="relative w-40 h-40 lg:w-48 lg:h-48">
            <svg className="w-40 h-40 lg:w-48 lg:h-48 transform -rotate-90" viewBox="0 0 42 42">
              <circle
                cx="21"
                cy="21"
                r="15.915"
                fill="transparent"
                stroke="#e5e7eb"
                className="dark:stroke-gray-700"
                strokeWidth="3"
              />
              {categoryData.reduce((acc, category, index) => {
                const previousPercent = acc;
                const strokeDasharray = `${category.percentage} ${100 - category.percentage}`;
                const strokeDashoffset = -previousPercent;
                
                acc += category.percentage;
                
                return (
                  acc,
                  <circle
                    key={category.category}
                    cx="21"
                    cy="21"
                    r="15.915"
                    fill="transparent"
                    stroke={category.color}
                    strokeWidth="3"
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                  />
                );
              }, 0)}
            </svg>
          </div>
        </div>
        
        {/* Legend */}
        <div className="w-full max-w-md">
          <div className="space-y-3">
            {categoryData.map((category) => {
              // Calculate progress based on limit amount OR target percentage
              let progressPercentage = 0;
              let progressLabel = '';
              let isOverBudget = false;
              let isNearLimit = false;

              if (category.limitAmount && category.limitAmount > 0) {
                // Priority 1: Fixed Limit Amount
                const targetAmount = category.limitAmount;
                progressPercentage = (category.amount / targetAmount) * 100;
                progressLabel = `${progressPercentage.toFixed(1)}% of ${formatCurrency(targetAmount)}`;
                isOverBudget = progressPercentage >= 100;
                isNearLimit = progressPercentage >= 80 && progressPercentage < 100;
              } else if (category.targetPercentage && category.targetPercentage > 0 && monthlyIncome && monthlyIncome > 0) {
                // Priority 2: Target Percentage of Income
                const targetAmount = monthlyIncome * (category.targetPercentage / 100);
                progressPercentage = (category.amount / targetAmount) * 100;
                progressLabel = `${progressPercentage.toFixed(1)}% of target`;
                isOverBudget = progressPercentage >= 100;
                isNearLimit = progressPercentage >= 80 && progressPercentage < 100;
              } else {
                // Fallback: Percentage of total spending
                progressPercentage = category.percentage;
                progressLabel = `${category.percentage.toFixed(1)}% of total`;
              }

              const isExpanded = expandedCategories.has(category.category);
              const hasTransactions = category.transactions && category.transactions.length > 0;

              return (
                <div key={category.category} className="flex flex-col bg-gray-50 dark:bg-gray-700/50 rounded-lg overflow-hidden">
                  <div 
                    className={clsx(
                      "flex flex-col p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors",
                      hasTransactions && "cursor-pointer"
                    )}
                    onClick={() => hasTransactions && toggleExpand(category.category)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        {hasTransactions && (
                          <div className="text-gray-400 dark:text-gray-500">
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </div>
                        )}
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0 border border-white dark:border-gray-600 shadow-sm"
                          style={{ backgroundColor: category.color }}
                        ></div>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900 dark:text-white leading-tight">
                            {category.category}
                          </span>
                          {category.limitAmount ? (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Limit: {formatCurrency(category.limitAmount)}
                            </span>
                          ) : category.targetPercentage && category.targetPercentage > 0 ? (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Target: {category.targetPercentage}%
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <div className="text-sm font-semibold text-gray-900 dark:text-white whitespace-nowrap">
                          {formatCurrency(category.amount)}
                        </div>
                        <div className="flex flex-col items-end">
                          <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {progressLabel}
                          </div>
                          {isNearLimit && (
                            <span className="text-[10px] font-bold text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30 px-1.5 py-0.5 rounded-full mt-0.5">
                              Near Limit
                            </span>
                          )}
                          {isOverBudget && (category.limitAmount || category.targetPercentage) && (
                            <span className="text-[10px] font-bold text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 px-1.5 py-0.5 rounded-full mt-0.5">
                              Over Limit
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5 overflow-hidden">
                      <div 
                        className={`h-1.5 rounded-full transition-all duration-500 ease-out ${
                          isOverBudget ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : ''
                        }`}
                        style={{ 
                          width: `${Math.min(Math.max(progressPercentage, 2), 100)}%`,
                          backgroundColor: (isOverBudget || isNearLimit) ? undefined : category.color 
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Transactions List */}
                  {isExpanded && hasTransactions && (
                    <div className="border-t dark:border-gray-600 bg-white dark:bg-gray-800">
                      <div className="divide-y divide-gray-100 dark:divide-gray-700">
                        {category.transactions!.map(transaction => (
                          <div key={transaction.id} className="p-3 pl-12 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">{transaction.description}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {transaction.merchant} • {new Date(transaction.date).toLocaleDateString()}
                                </p>
                              </div>
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                {formatCurrency(transaction.amount)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}